import React, { useState } from 'react';

const MERCOR_PURPLE = '#5B3DC5';

const PrivacyCookiesModal = ({ isOpen, onClose, onConfirm }) => {
  const [analytics, setAnalytics] = useState(true);
  const [essentialServices, setEssentialServices] = useState(true);
  const [targetedAds, setTargetedAds] = useState(true);
  const [expandedSection, setExpandedSection] = useState(null);

  if (!isOpen) return null;

  const handleRejectAll = () => { setAnalytics(false); setTargetedAds(false); };
  const handleAcceptAll = () => { setAnalytics(true); setTargetedAds(true); };
  const handleConfirm = () => {
    if (onConfirm) onConfirm({ analytics, essentialServices, targetedAds });
    onClose();
  };

  const ToggleSwitch = ({ enabled, onChange, disabled = false }) => (
    <button
      onClick={() => !disabled && onChange(!enabled)}
      className={`relative shrink-0 transition-colors duration-200 ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
      style={{
        width: '44px',
        height: '24px',
        borderRadius: '12px',
        backgroundColor: enabled ? MERCOR_PURPLE : '#D1D5DB',
      }}
    >
      <div
        className="absolute top-[2px] flex items-center justify-center rounded-full bg-white shadow-sm transition-transform duration-200"
        style={{
          width: '20px',
          height: '20px',
          transform: enabled ? 'translateX(22px)' : 'translateX(2px)',
        }}
      >
        {enabled && (
          <svg className="w-3 h-3" viewBox="0 0 12 12" fill="none">
            <path d="M2 6L5 9L10 3" stroke={MERCOR_PURPLE} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </div>
    </button>
  );

  const CategoryRow = ({ label, enabled, onChange, disabled = false }) => (
    <div className="border-b border-gray-100 last:border-0">
      <div className="flex items-center justify-between px-6 py-5" style={{ backgroundColor: '#FAFAFA' }}>
        <div className="flex items-center gap-2.5">
          <button
            onClick={() => setExpandedSection(expandedSection === label ? null : label)}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className={`w-4 h-4 transition-transform duration-200 ${expandedSection === label ? 'rotate-90' : ''}`} viewBox="0 0 16 16" fill="none">
              <path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <span className="text-base font-bold text-gray-900">{label}</span>
        </div>
        <ToggleSwitch enabled={enabled} onChange={onChange} disabled={disabled} />
      </div>
      {expandedSection === label && (
        <div className="px-12 py-3 text-sm text-gray-500 bg-white leading-relaxed">
          {label === 'Analytics' && 'We use analytics cookies to understand how visitors interact with our platform, helping us improve features and user experience.'}
          {label === 'Essential Services' && 'These cookies are required for the platform to function properly and cannot be disabled.'}
          {label === 'Targeted Advertising' && 'We may use targeted advertising cookies to show you relevant job opportunities and features based on your browsing activity.'}
        </div>
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-[540px] mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-8 pt-8 pb-7">
          {/* Main heading */}
          <h1 className="text-[22px] font-bold text-gray-900 mb-7 leading-snug">
            Choose how we use your personal information
          </h1>

          {/* Privacy & Cookies */}
          <h2 className="text-xl font-bold text-gray-900 mb-3">Privacy & Cookies</h2>
          <p className="text-sm text-gray-500 leading-relaxed mb-7" style={{ lineHeight: '1.7' }}>
            Welcome! We're glad you're here and want you to know that we respect your privacy and your right to control
            how we collect, use, and share your personal data. Please read our{' '}
            <span className="text-gray-900 underline cursor-pointer" style={{ textUnderlineOffset: '2px' }}>Privacy Policy</span>
            {' '}to learn about our privacy practices.
          </p>

          {/* Purposes + Buttons */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-gray-900">Purposes</h3>
            <div className="flex items-center gap-2">
              <button
                onClick={handleRejectAll}
                className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Reject All
              </button>
              <button
                onClick={handleAcceptAll}
                className="px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 transition-opacity"
                style={{ backgroundColor: MERCOR_PURPLE }}
              >
                Accept All
              </button>
            </div>
          </div>

          {/* Vendors */}
          <div className="flex items-center gap-1 mb-5 cursor-pointer group">
            <span className="text-sm font-medium text-gray-900">Vendors</span>
            <span className="text-sm text-gray-400 ml-0.5">3</span>
            <svg className="w-4 h-4 text-gray-400 group-hover:text-gray-600" viewBox="0 0 16 16" fill="none">
              <path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>

          {/* Toggle Categories */}
          <div className="border border-gray-200 rounded-xl overflow-hidden mb-7">
            <CategoryRow label="Analytics" enabled={analytics} onChange={setAnalytics} />
            <CategoryRow label="Essential Services" enabled={essentialServices} onChange={() => {}} disabled={true} />
            <CategoryRow label="Targeted Advertising" enabled={targetedAds} onChange={setTargetedAds} />
          </div>

          {/* Confirm Button */}
          <button
            onClick={handleConfirm}
            className="w-full py-3.5 text-white font-semibold rounded-xl transition-opacity hover:opacity-90 text-sm"
            style={{ backgroundColor: MERCOR_PURPLE }}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

export default PrivacyCookiesModal;
